import {Button, ToggleButton} from '@wandb/components';
import {Icon} from '@wandb/weave/components/Icon';
import React from 'react';
import {twMerge} from 'tailwind-merge';

import {keyFromString} from '../../../util/runs';
import {getIconNameForKey} from '../../../util/uihelpers';
import {useDragOver} from './useDragOver';

type TableEditorFixedColumnFieldProps = {
  children: React.ReactNode;
  column: string;
  fixed: true;
  pinned: true;
};

type TableEditorColumnFieldUnfixedProps = {
  children: React.ReactNode;
  column: string;
  draggingColumnName: string | null;
  fixed?: false;
  onDragStart: (column: string) => void;
  onDrop: (column: string) => void;
  onMoveClick: (column: string) => void;
  onPinClick: (column: string) => void;
  pinned?: boolean;
  hidden?: boolean;
  isDisabled?: boolean;
};

type TableEditorColumnFieldProps =
  | TableEditorColumnFieldUnfixedProps
  | TableEditorFixedColumnFieldProps;

export function TableEditorColumnField(props: TableEditorColumnFieldProps) {
  const {children, column, fixed, pinned} = props;

  const key = keyFromString(column)!;
  const {getDraggingProps, isDraggingOver} = useDragOver({
    onDragStart() {
      if ('onDragStart' in props) {
        props.onDragStart(column);
      }
    },
    onDrop() {
      if ('onDrop' in props) {
        props.onDrop(column);
      }
    },
  });

  // NOTE: do NOT set items-center because long column names will wrap,
  // and icons/buttons should be top aligned when this happens.
  const baseClasses = 'group flex py-6 gap-10 rounded-md px-10';

  // Column is fixed and pinned—probably the "Disply Name" column. It cannot
  // be dragged or dropped, and it is always visible:
  if (fixed && pinned) {
    return (
      <div className={baseClasses}>
        <Icon className="my-2" name={getIconNameForKey(key)} />
        <div className="grow self-center">{children}</div>
        <Icon name="lock-closed" className="my-4 h-16 w-16" />
        <Icon
          name="pin-to-left-filled"
          className="m-4 h-16 w-16 text-wb-accent"
        />
      </div>
    );
  }

  const {draggingColumnName, hidden, isDisabled, onMoveClick, onPinClick} =
    props;

  const cls = twMerge(
    baseClasses,
    'hover:bg-moon-150 dark:hover:bg-moon-800 focus-within:bg-moon-150 dark:focus-within:bg-moon-800',
    !fixed && 'cursor-grab',
    draggingColumnName === column && 'cursor-grabbing',
    isDisabled && 'pointer-events-none'
  );
  const showHideControlCls = twMerge(
    'focus-within:opacity-100 group-hover:opacity-100 opacity-0 transition-all'
  );
  const pinControlCls = twMerge(
    'h-24 transition-all focus-within:opacity-100 group-hover:opacity-100',
    !pinned && 'opacity-0'
  );

  const label = `${pinned ? 'Unpin' : 'Pin'} ${key.name} column`;
  const draggingProps = isDisabled ? {} : getDraggingProps();

  return (
    <div style={{display: 'contents'}} {...draggingProps}>
      {hidden ? null : (
        <div
          className={twMerge(
            'transition-all',
            isDraggingOver && 'h-36',
            !isDraggingOver && 'h-0'
          )}
          aria-hidden
        />
      )}
      <div draggable={isDisabled ? undefined : 'true'} className={cls}>
        <Icon
          name={getIconNameForKey(key)}
          className="my-2 group-hover:hidden"
        />
        <Icon name="drag-grip" className="my-2 hidden group-hover:block" />
        <div className="text-wrap grow self-center break-all">{children}</div>
        <div className={showHideControlCls}>
          <Button
            size="sm"
            variant="secondary"
            onPress={() => onMoveClick(column)}
            isDisabled={isDisabled}>
            {hidden ? 'Show' : 'Hide'}
          </Button>
        </div>
        <div className={pinControlCls}>
          <ToggleButton
            aria-label={label}
            variant="pin"
            size="sm"
            isDisabled={isDisabled}
            isSelected={pinned}
            onPress={() => !isDisabled && onPinClick(column)}
          />
        </div>
      </div>
    </div>
  );
}
